/**
 * This section composes query-wide paid-media cards and charts without coupling
 * their availability or refresh state to the existing report table.
 */
import React, { useMemo, useState } from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Tooltip from '@amzn/meridian/tooltip';
import { type TranslateFn, useTranslation } from 'src/arb-wrapper';
import { MetricCard } from 'src/components/entity-metrics/MetricCard';
import { skeletonBase } from 'src/components/entity-metrics/skeleton';
import {
  type PaidMediaInsightsArgs,
  type PaidMediaInsightsSummary,
  useQueryPaidMediaInsightsQuery,
} from 'src/store/leela-api';
import {
  getDefaultTrendPreset,
  getTrendRange,
  InsightsCharts,
  type TrendPresetValue,
} from './InsightsCharts';
import { InsightErrorCard } from './InsightErrorCard';
import { RefreshLoader } from './RefreshLoader';

export interface PaidMediaCardMetric {
  readonly key: string;
  readonly label: string;
  readonly numberFormat: Intl.NumberFormatOptions;
}

export interface PaidMediaEngagementMetric {
  readonly key: string;
  readonly label: string;
}

export interface PaidMediaInsightsConfig {
  readonly cardMetrics: readonly PaidMediaCardMetric[];
  readonly engagementMetrics: readonly PaidMediaEngagementMetric[];
}

export interface InsightsSectionProps {
  readonly config: PaidMediaInsightsConfig;
  readonly displayCurrency?: string;
  readonly filters: Record<string, string | string[]>;
  readonly view: string;
}

interface SummaryMetricCardProps {
  readonly currency: string;
  readonly label: string;
  readonly metric: PaidMediaCardMetric;
  readonly tooltip?: string;
  readonly value: number;
}

const CARD_HEIGHT = 120;
const CARD_FLEX = '1 1 180px';

const buildQueryArgs = ({
  displayCurrency,
  filters,
  trendPreset,
  view,
}: Omit<InsightsSectionProps, 'config'> & {
  readonly trendPreset: TrendPresetValue;
}): PaidMediaInsightsArgs => ({
  view,
  filters,
  ...(displayCurrency && { displayOptions: { currency: displayCurrency } }),
  sections: {
    summary: {},
    // The backend enforces the trend-only 366-day range cap.
    trend: { filters: getTrendRange(trendPreset) },
  },
});

const getNumberFormat = (
  numberFormat: Intl.NumberFormatOptions,
  currency: string,
): Intl.NumberFormatOptions => numberFormat.style === 'currency'
  ? { ...numberFormat, currency }
  : numberFormat;

const getEngagementData = (
  summary: PaidMediaInsightsSummary | undefined,
  engagementMetrics: readonly PaidMediaEngagementMetric[],
  translate: TranslateFn,
): Array<{ label: string; value: number }> => engagementMetrics.map((metric) => ({
  label: translate(metric.label),
  value: summary?.totals[metric.key] ?? 0,
}));

const getSpendTooltip = (
  appliedCurrency: string | undefined,
  displayCurrency: string | undefined,
  translate: TranslateFn,
): string | undefined => {
  if (!appliedCurrency) return undefined;
  return displayCurrency
    ? translate('paid_media_insights_spend_converted_currency', { currency: appliedCurrency })
    : translate('paid_media_insights_spend_default_currency');
};

const SummaryMetricCard: React.FC<SummaryMetricCardProps> = ({
  currency,
  label,
  metric,
  tooltip,
  value,
}) => {
  const card = (
    <div data-testid={`paid-media-insights-card-${metric.key}`} style={{ flex: CARD_FLEX }}>
      <MetricCard
        label={label}
        numberFormat={getNumberFormat(metric.numberFormat, currency)}
        value={value}
      />
    </div>
  );

  return tooltip
    ? <Tooltip position="top" title={tooltip}>{card}</Tooltip>
    : card;
};

interface SummaryCardSkeletonsProps {
  readonly cardMetrics: readonly PaidMediaCardMetric[];
}

const SummaryCardSkeletons: React.FC<SummaryCardSkeletonsProps> = ({ cardMetrics }) => (
  <>
    {cardMetrics.map(({ key }) => (
      <div
        data-testid={`paid-media-insights-card-skeleton-${key}`}
        key={key}
        style={{ ...skeletonBase, flex: CARD_FLEX, height: CARD_HEIGHT }}
      />
    ))}
  </>
);

interface SummaryCardsProps {
  readonly cardMetrics: readonly PaidMediaCardMetric[];
  readonly isLoading: boolean;
  readonly showRefreshLoader: boolean;
  readonly spendTooltip?: string;
  readonly summary?: PaidMediaInsightsSummary;
  readonly translate: TranslateFn;
}

const SummaryCards: React.FC<SummaryCardsProps> = ({
  cardMetrics,
  isLoading,
  showRefreshLoader,
  spendTooltip,
  summary,
  translate,
}) => (
  <div data-testid="paid-media-insights-metric-container" style={{ position: 'relative' }}>
    <Row data-testid="paid-media-insights-metric-row" spacing="300">
      {isLoading
        ? <SummaryCardSkeletons cardMetrics={cardMetrics} />
        : summary && cardMetrics.map((metric) => (
          <SummaryMetricCard
            currency={summary.currency}
            key={metric.key}
            label={translate(metric.label, { currency: summary.currency })}
            metric={metric}
            tooltip={metric.key === 'spend' ? spendTooltip : undefined}
            value={summary.totals[metric.key] ?? 0}
          />
        ))}
    </Row>
    {showRefreshLoader && (
      <RefreshLoader testId="paid-media-insights-cards-refresh-loader" />
    )}
  </div>
);

const useInsightsSectionData = ({
  config,
  displayCurrency,
  filters,
  view,
}: InsightsSectionProps) => {
  const { t } = useTranslation();
  const [trendPreset, setTrendPreset] = useState<TrendPresetValue>(
    getDefaultTrendPreset,
  );
  const queryArgs = useMemo(
    () => buildQueryArgs({ displayCurrency, filters, trendPreset, view }),
    [displayCurrency, filters, trendPreset, view],
  );
  const query = useQueryPaidMediaInsightsQuery(queryArgs);
  const summary = query.data?.sections.summary;
  const trend = query.data?.sections.trend;
  const engagementData = useMemo(
    () => getEngagementData(summary, config.engagementMetrics, t),
    [config.engagementMetrics, summary, t],
  );

  return {
    engagementData,
    query,
    showErrorCard: query.isError && query.data === undefined,
    showRefreshLoader: query.isFetching && !query.isLoading && query.data !== undefined,
    spendTooltip: getSpendTooltip(summary?.currency, displayCurrency, t),
    summary,
    trend,
    trendPreset,
    setTrendPreset,
  };
};

export const InsightsSection: React.FC<InsightsSectionProps> = (props) => {
  const { t } = useTranslation();
  const insights = useInsightsSectionData(props);

  if (insights.showErrorCard) {
    return (
      <Column data-testid="paid-media-insights" width="100%">
        <InsightErrorCard
          message={t('paid_media_insights_error')}
          minHeight={CARD_HEIGHT}
          testId="paid-media-insights-error"
        />
      </Column>
    );
  }

  return (
    <Column data-testid="paid-media-insights" spacing="300" width="100%">
      <SummaryCards
        cardMetrics={props.config.cardMetrics}
        isLoading={insights.query.isLoading}
        showRefreshLoader={insights.showRefreshLoader}
        spendTooltip={insights.spendTooltip}
        summary={insights.summary}
        translate={t}
      />
      <InsightsCharts
        engagementData={insights.engagementData}
        isLoading={insights.query.isLoading}
        showRefreshLoader={insights.showRefreshLoader}
        trend={insights.trend}
        trendPreset={insights.trendPreset}
        onTrendPresetChange={insights.setTrendPreset}
      />
    </Column>
  );
};
